// @flow
import type { Node } from 'react';
import styled from '@emotion/styled';
import { color, SPACING, typography } from '@tablekit/theme';
import { createHeader } from '@tablekit/typography';
import LazyLoad from 'react-lazyload';
import type { BlokComponentPropsType } from 'components/types';
import renderComponent from 'components/editable/renderComponent';
import YoutubeVideo from 'components/editable/YoutubeVideo';
import SatoriCode from 'components/editable/SatoriCode';
import Callout from 'components/editable/Callout';
import { imageTransform } from 'components/Image';
import RichText from 'components/RichText';
import { LAZY_LOAD_OFFSET, MOBILE_BREAKPOINT } from 'utils/theme';
import type {
  BlogBodyType,
  BlogQuoteType,
  BlogParagraphType,
  BlogImageType
} from './types';
import * as componentKeys from '../../../../componentKeys';

type PropsType = {
  content?: BlogBodyType
};

const BlogSection = styled.div`
  margin: ${SPACING.L6} 0;
`;
const ParagraphHeader = styled(createHeader('h3', 'L6'))`
  margin-bottom: ${SPACING.L4};
  @media ${MOBILE_BREAKPOINT} {
    margin-bottom: ${SPACING.L2};
  }
`;
const ParagraphContent = styled(RichText)`
  & > p:not(:last-child) {
    margin-bottom: ${SPACING.L4};
  }

  & > ul {
    list-style: unset;
    margin-left: ${SPACING.L6};

    & > li {
      margin-bottom: ${SPACING.L4};
    }
  }

  ${typography('L3')};
  @media ${MOBILE_BREAKPOINT} {
    ${typography('L2')};
  }
`;
const BlogQuote = styled.div`
  border-left: 10px solid ${color('primary')};
  padding: ${SPACING.L5};
  font-style: italic;
  ${typography('L7')}
  @media ${MOBILE_BREAKPOINT} {
    margin-left: ${SPACING.L6};
    padding: ${SPACING.L4};
    ${typography('L4')};
  }
`;
const ImageSection = styled(BlogSection)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const Image = styled.img`
  max-height: 480px;
  max-width: 100%;
`;
const ImageSubtitle = styled.div`
  font-style: italic;
  margin-top: ${SPACING.L2};
  text-align: center;
`;

const componentMap = {
  // These images are forced to a RATIO but should just be displayed at original
  // size, this allows larger images to be opened but shown smaller
  [componentKeys.BLOG_IMAGE]: ({
    blok: { image, subtitle }
  }: BlokComponentPropsType<BlogImageType>) => (
    <ImageSection>
      <a href={image} target="_blank" rel="noopener noreferrer">
        <LazyLoad offset={LAZY_LOAD_OFFSET} height={480}>
          <Image
            alt={subtitle}
            src={imageTransform(image, { size: { width: 895, height: 480 } })}
          />
        </LazyLoad>
      </a>
      {subtitle && <ImageSubtitle>{subtitle}</ImageSubtitle>}
    </ImageSection>
  ),
  [componentKeys.BLOG_PARAGRAPH]: ({
    blok: { heading, paragraph }
  }: BlokComponentPropsType<BlogParagraphType>) => (
    <BlogSection>
      {heading && <ParagraphHeader>{heading}</ParagraphHeader>}
      <ParagraphContent richText={paragraph} />
    </BlogSection>
  ),
  [componentKeys.BLOG_QUOTE]: ({
    blok: { quote }
  }: BlokComponentPropsType<BlogQuoteType>) => (
    <BlogSection>
      <BlogQuote>&ldquo;{quote}&rdquo;</BlogQuote>
    </BlogSection>
  ),
  [componentKeys.CALLOUT]: Callout,
  [componentKeys.YOUTUBE_VIDEO]: YoutubeVideo,
  [componentKeys.SATORI_CODE]: SatoriCode
};

const Content = ({ content }: PropsType) =>
  (content || []).map<Node>((contentBlok) =>
    renderComponent(
      contentBlok,
      {
        key: contentBlok._uid,
        customPath: 'components/editable/blog/Post.jsx'
      },
      componentMap
    )
  );

export default Content;
